<template>
  <div>
    <h2 class="text-[20px] font-semibold mb-2 overflow-hidden text-ellipsis">
      Edit Mailboxes
    </h2>
    <p
      class="secondary_color text-[14px] font-normal leading-[17px] overflow-hidden text-ellipsis mb-6"
    >
      Edit your generated mailboxes before proceeding to checkout
    </p>

    <div
      v-if="mailboxes.length === 0"
      class="flex flex-col items-center justify-center py-16"
    >
      <img
        :src="imagePath.emptyScreenGreyDarkIcon"
        alt="No mailboxes"
        class="w-24 h-24 mb-4"
      />
      <p class="secondary_color text-[16px] text-center">
        No mailboxes were generated. Please go back and make sure to select
        valid domains.
      </p>
    </div>

    <div v-else class="flex flex-col gap-6">
      <div v-for="(mailbox, index) in mailboxes" :key="index">
        <CustomContainer padding="p-0">
          <div class="flex justify-between items-center mb-6 border_bottom p-4">
            <div
              class="flex items-center gap-1 font-semibold text-[14px] leading-[17px]"
            >
              <div>{{ index + 1 }}.</div>
              <span> {{ mailbox.username }}@{{ mailbox.domain }} </span>
            </div>
            <div class="flex items-center gap-2">
              <img
                :src="imagePath.neutral.grey.deleteIconGrey"
                alt="delete"
                class="w-4 h-4 cursor-pointer hover:opacity-80"
                @click="removeMailbox(mailbox.id)"
              />
            </div>
          </div>

          <div class="px-4">
            <div class="flex gap-4 mb-6">
              <div class="w-1/2">
                <label
                  class="block secondary_color text-[13px] font-normal mb-2"
                  >First Name</label
                >
                <a-input
                  v-model:value="mailbox.firstName"
                  placeholder="First Name"
                  class="w-full input_medium"
                />
              </div>
              <div class="w-1/2">
                <label
                  class="block secondary_color text-[13px] font-normal mb-2"
                  >Last Name</label
                >
                <a-input
                  v-model:value="mailbox.lastName"
                  placeholder="Last Name"
                  class="w-full input_medium"
                />
              </div>
            </div>

            <div class="flex gap-4 mb-6">
              <div class="w-1/2">
                <label
                  class="block secondary_color text-[13px] font-normal mb-2"
                  >Email Address</label
                >
                <div class="relative">
                  <a-input
                    v-model:value="mailbox.username"
                    placeholder="Username"
                    class="w-full input_medium pr-32"
                  />
                  <span class="absolute right-3 top-2.5 secondary_color text-xs"
                    >@{{ mailbox.domain }}</span
                  >
                </div>
              </div>
              <div class="w-1/2">
                <label
                  class="block secondary_color text-[13px] font-normal mb-2"
                  >Domain</label
                >
                <a-select
                  v-model:value="mailbox.domain"
                  placeholder="Select domain"
                  class="w-full"
                  :options="domainOptions"
                >
                  <template #suffixIcon>
                    <ThemeImage
                      :icon="imageNames.BOTTOM_ARROW"
                      alt="arrow"
                      class="w-4 h-4"
                    />
                  </template>
                </a-select>
              </div>
            </div>

            <div>
              <div
                class="flex items-center gap-1 cursor-pointer mb-6"
                @click="toggleSignatureSection(mailbox.id)"
              >
                <div :class="{ 'rotate-90': mailbox.showSignatureSection }">
                  <ThemeImage
                    :icon="imageNames.RIGHT_ARROW"
                    alt="arrow"
                    class="w-4 h-4"
                  />
                </div>
                <span class="text-[14px] font-semibold"
                  >Signature & Forwarding</span
                >
              </div>

              <div
                v-if="mailbox.showSignatureSection"
                class="transition-all duration-300"
              >
                <div class="mb-6">
                  <SignatureEditor
                    :modelValue="
                      parseSignatureTemplate(
                        mailbox.signature,
                        mailbox.firstName,
                        mailbox.lastName,
                      )
                    "
                    @update:modelValue="updateSignature(mailbox, $event)"
                    :height="340"
                  />
                </div>

                <div class="mb-4 w-[50%]">
                  <label
                    class="block secondary_color text-[13px] font-normal leading-[15px] mb-3"
                    >Forwarding email</label
                  >
                  <a-input
                    v-model:value="mailbox.forwardingEmail"
                    placeholder="Enter forwarding email address"
                    class="w-full input_medium"
                  />
                </div>
              </div>
            </div>
          </div>
        </CustomContainer>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted } from "vue";
import { message } from "ant-design-vue";
import imagePath from "@/src/paths/imagePath.js";
import CustomContainer from "@/src/components/CustomContainer.vue";
import SignatureEditor from "@/src/components/signature_editor/SignatureEditor.vue";
import { imageNames } from "@/src/constants/imageConstants.js";
import { validateEmail, parseSignatureTemplate } from "@/src/utils/utils.js";

const props = defineProps({
  storedSignatureSettings: {
    type: Object,
    default: () => ({
      signature: "",
      enableSignature: true,
      enableForwarding: false,
      forwardingEmail: "",
    }),
  },

  responseData: {
    type: Object,
    default: () => ({}),
  },

  preservedMailboxes: {
    type: Array,
    default: () => [],
  },
});

const emit = defineEmits(["next-step", "update:signatureSettings"]);

const mailboxesData = ref({});
const mailboxes = ref([]);
const domainOptions = ref([]);

const processMailboxesData = (data) => {
  const result = [];
  const domains = new Set();

  if (!data) {
    return [];
  }

  try {
    const domainsObject = data.domains || data;

    if (!domainsObject || typeof domainsObject !== "object") {
      return [];
    }

    Object.keys(domainsObject).forEach((domain) => {
      domains.add(domain);

      if (!Array.isArray(domainsObject[domain])) {
        return;
      }

      domainsObject[domain].forEach((mailbox) => {
        if (!mailbox) {
          return;
        }

        const processedMailbox = {
          id: mailbox.id || Math.random().toString(36).substr(2, 9),
          domain: domain,
          username: mailbox.username || "",
          firstName: mailbox.firstName || "",
          lastName: mailbox.lastName || "",
          signature: mailbox.signature || "",
          forwardingEmail: mailbox.forwardingEmail || "",
          showSignatureSection: mailbox.showSignatureSection || false,
        };

        Object.keys(mailbox).forEach((key) => {
          if (!processedMailbox.hasOwnProperty(key)) {
            processedMailbox[key] = mailbox[key];
          }
        });

        result.push(processedMailbox);
      });
    });

    domainOptions.value = [];
    domains.forEach((domain) => {
      domainOptions.value.push({ value: domain, label: domain });
    });

    return result;
  } catch (error) {
    return [];
  }
};

onMounted(() => {
  try {
    if (mailboxes.value.length > 0) {
      return;
    }

    if (props.preservedMailboxes && props.preservedMailboxes.length > 0) {
      mailboxes.value = JSON.parse(JSON.stringify(props.preservedMailboxes));

      const domains = new Set();
      props.preservedMailboxes.forEach((mailbox) => {
        if (mailbox.domain) {
          domains.add(mailbox.domain);
        }
      });

      domainOptions.value = [];
      domains.forEach((domain) => {
        domainOptions.value.push({ value: domain, label: domain });
      });
    } else if (
      props.responseData &&
      (Object.keys(props.responseData).length > 0 || props.responseData.domains)
    ) {
      initializeWithData(props.responseData);
    } else {
      message.error("No mailbox data available");
    }
  } catch (error) {
    message.error("Error initializing mailbox data: " + error.message);
  }
});

const initializeWithData = (data) => {
  try {
    if (data && (Object.keys(data).length > 0 || data.domains)) {
      mailboxesData.value = data;

      if (mailboxes.value.length === 0) {
        const processedData = processMailboxesData(data);
        if (processedData.length > 0) {
          const hasUIState = processedData.some(
            (m) =>
              m.hasOwnProperty("showSignatureSection") &&
              m.hasOwnProperty("id"),
          );

          mailboxes.value = processedData;
        } else {
          message.warning("Processing data returned zero mailboxes");
        }
      }
    } else {
      if (mailboxes.value.length === 0) {
        message.error("No valid mailbox data received");
      }
    }
  } catch (error) {
    if (mailboxes.value.length === 0) {
      message.error("Error processing mailbox data: " + error.message);
    }
  }
};

const updateSignature = (mailbox, newSignature) => {
  mailbox.signature = newSignature;
};

const toggleSignatureSection = (id) => {
  const mailbox = mailboxes.value.find((m) => m.id === id);
  if (mailbox) {
    mailbox.showSignatureSection = !mailbox.showSignatureSection;
  }
};

const removeMailbox = (id) => {
  const index = mailboxes.value.findIndex((m) => m.id === id);
  if (index !== -1) {
    mailboxes.value.splice(index, 1);
    message.success("Mailbox removed successfully");
  }
};

const validateBeforeNext = () => {
  for (const mailbox of mailboxes.value) {
    if (!mailbox.forwardingEmail) {
      message.error(
        `Forwarding email is required for ${mailbox.username}@${mailbox.domain}`,
      );
      return false;
    }

    if (!validateEmail(mailbox.forwardingEmail)) {
      message.error(
        `Invalid forwarding email for ${mailbox.username}@${mailbox.domain}`,
      );
      return false;
    }

    if (!mailbox.username || !mailbox.domain) {
      message.error("All mailboxes must have username and domain");
      return false;
    }
  }

  return true;
};

const getFormattedData = () => {
  const formattedData = {
    domains: [],
  };

  const domainMailboxes = {};

  mailboxes.value.forEach((mailbox) => {
    if (!domainMailboxes[mailbox.domain]) {
      domainMailboxes[mailbox.domain] = [];
    }

    domainMailboxes[mailbox.domain].push({
      email: `${mailbox.username}@${mailbox.domain}`,
      firstName: mailbox.firstName,
      lastName: mailbox.lastName,
      signature: parseSignatureTemplate(
        mailbox.signature,
        mailbox.firstName,
        mailbox.lastName,
      ),
      forwardingEmail: mailbox.forwardingEmail,
    });
  });

  Object.keys(domainMailboxes).forEach((domain) => {
    formattedData.domains.push({
      domain: domain,
      mailboxes: domainMailboxes[domain],
    });
  });

  return formattedData;
};

defineExpose({
  validateBeforeNext,
  getFormattedData,
  initializeWithData,
  getMailboxes: () => mailboxes.value,
  getSignature: () => null,
  getForwardingEmail: () => null,
  getNameType: () => null,
});
</script>
